import { useState } from 'react';
import { Calendar, Gauge, ClipboardList } from 'lucide-react';
import type { CustomerServiceRecord } from './types';
import { formatServiceMileage, type ServiceMileageSource } from '../mileage';

const PAGE_SIZE = 10;

interface ServiceHistoryTabProps {
    serviceHistory: CustomerServiceRecord[];
}

export function ServiceHistoryTab({ serviceHistory }: ServiceHistoryTabProps) {
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const visible = serviceHistory.slice(0, visibleCount);

    return (
        <section className="bg-panel border border-line rounded-xl overflow-hidden">
            <div className="p-4 border-b border-line flex items-center justify-between bg-raised">
                <h3 className="font-bold text-fg flex items-center gap-2">
                    <ClipboardList className="w-4 h-4 text-accent" />
                    Service History
                </h3>
                {serviceHistory.length > 0 && (
                    <span className="text-xs text-fg-4">{serviceHistory.length} record{serviceHistory.length !== 1 ? 's' : ''}</span>
                )}
            </div>
            <div className="divide-y divide-tint/5">
                {serviceHistory.length === 0 ? (
                    <div className="p-8 text-center text-fg-4 text-sm">
                        No completed services yet. A record is saved automatically each time an invoice is paid.
                    </div>
                ) : (
                    visible.map(rec => (
                        <div key={rec.id} className="p-4 hover:bg-tint/5 transition-colors">
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 text-xs text-fg-3">
                                        <Calendar className="w-3.5 h-3.5 text-fg-4" />
                                        {new Date(rec.service_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                        {(() => {
                                            const m = formatServiceMileage(rec.mileage, rec.mileage_source as ServiceMileageSource);
                                            return m && (
                                                <span className="flex items-center gap-1 ml-2">
                                                    <Gauge className="w-3.5 h-3.5" />
                                                    {m.value}
                                                    {m.qualifier && <span className="text-fg-4">{m.qualifier}</span>}
                                                </span>
                                            );
                                        })()}
                                    </div>
                                    <h4 className="font-bold text-fg mt-1 truncate">{rec.title}</h4>
                                    {rec.summary && <p className="text-sm text-fg-3 line-clamp-2 mt-0.5">{rec.summary}</p>}
                                    {Array.isArray(rec.line_items) && rec.line_items.length > 0 && (
                                        <ul className="mt-2 space-y-0.5">
                                            {rec.line_items.slice(0, 4).map((li, i) => (
                                                <li key={i} className="text-xs text-fg-4 flex justify-between gap-3">
                                                    <span className="truncate">{li.quantity}× {li.description || 'Item'}</span>
                                                    <span className="font-mono text-fg-3">£{Number(li.total || 0).toFixed(2)}</span>
                                                </li>
                                            ))}
                                            {rec.line_items.length > 4 && (
                                                <li className="text-xs text-fg-4">+{rec.line_items.length - 4} more</li>
                                            )}
                                        </ul>
                                    )}
                                </div>
                                <div className="text-right shrink-0">
                                    <div className="text-sm font-mono font-bold text-fg">£{Number(rec.total || 0).toFixed(2)}</div>
                                    <div className="text-fd-3xs uppercase font-bold text-success mt-1">Paid</div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
            {serviceHistory.length > visibleCount && (
                <button
                    onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                    className="w-full p-3 text-xs font-bold text-accent hover:bg-tint/5 transition-colors border-t border-tint/5"
                >
                    Load more ({serviceHistory.length - visibleCount} remaining)
                </button>
            )}
        </section>
    );
}
